//@author: devin

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    NgZone,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewEncapsulation,
    Input,
    ViewChild,
} from "@angular/core";
import { S25EditableAbstract } from "../s25.editable.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Util } from "../../../util/s25-util";
import { AttachmentService } from "../../../services/attachment.service";
import { S25File, FileUploadComponent } from "../../s25-file-upload/s25.file.upload.component";

@TypeManagerDecorator("s25-ng-editable-file")
@Component({
    selector: "s25-ng-editable-file",
    template: `
        @if (init) {
            <div class="editable-file-section">
                @if (model?.files) {
                    @for (file of model?.files; track file.itemId) {
                        <s25-ng-display-file
                            [fileName]="file.itemName"
                            [objectId]="file.objectId"
                            [objectType]="file.objectTypeId"
                            [file]="file"
                            hasRemoveButton="true"
                            (onRemove)="remove($event)"
                        ></s25-ng-display-file>
                    }
                }

                <s25-ng-file-upload [multiple]="true" [showFiles]="false" [onAdd]="handleFileUpload">
                </s25-ng-file-upload>
            </div>

            <!-- some callers are NOT calling the file store service with each upload, so we cannot show links for them... -->
            <!-- <a data-ng-if="::self.model.showLinks" target="_self" download="{{::self.encodedName(item.itemName)}}" href="{{::self.getUrl(item)}}">{{::item.itemName}}</a> -->

            <!-- some callers are NOT calling the file store service with each upload, so we just show names for them... -->
            <!-- <span data-ng-if="::!self.model.showLinks">{{::item.itemName}}</span> -->
        }
    `,
    styles: `
        .editable-file-section {
            display: block;
            text-align: left; /* Make inline content (like text or buttons) align left */
            align-items: flex-start; /* If in flex layout parent */
            justify-content: flex-start;
            padding-left: 0; /* Prevent unexpected indent */
            margin-left: 0;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditableFileComponent extends S25EditableAbstract<string> implements OnInit {
    @ViewChild(FileUploadComponent) fileUploadComponent: FileUploadComponent;
    @Input() model?: {
        files: AttachmentItem[];
        itemTypeId: number;
        itemId: number;
        addAction: (file: any) => Promise<void>;
        removeAction: (file: any) => Promise<void>;
        showLinks?: boolean;
    };

    init = false;

    getType = () => "file";

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
        private renderer: Renderer2,
    ) {
        super(elementRef, cd, zone, renderer);
    }

    ngOnInit(): void {
        this.model.showLinks = S25Util.coalesce(this.model.showLinks, true);
        this.init = true;
        this.cd.detectChanges();
    }

    getUrl(item: any): string {
        return AttachmentService.getFileUrl(this.model.itemTypeId, this.model.itemId, item.itemName);
    }

    encodedName(itemName: string): string {
        return encodeURIComponent(itemName);
    }

    getExtension(itemName: string): string {
        const parts = itemName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    async remove(file: AttachmentItem): Promise<void> {
        await this.model.removeAction(file);
        this.removeFromList(file);
    }

    private removeFromList(file: AttachmentItem): void {
        S25Util.array.remove(this.model.files, this.model.files.indexOf(file));
        this.cd.detectChanges();
    }

    handleFileUpload = async (file: S25File) => {
        const getFilesList = await this.getFiles();
        const isNewFileExit = getFilesList.find((f) => f.itemName === file.name);
        if (isNewFileExit) {
            alert("You have already uploaded a file with this name.");
            return;
        } else {
            this.validated(file);
            this.add(file);
        }
    };

    async add(file: any): Promise<void> {
        file.itemName = file.name;

        try {
            await this.model.addAction(file);
            this.model.files = await this.getFiles();
        } catch (err) {
            this.removeFromList(file);
            S25Util.showError(err);
        }

        this.cd.detectChanges();
    }

    validated(file: S25File): string | undefined {
        if (!file.name) {
            return "File name cannot be empty";
        }
        if (file.name.length > 112) {
            return "The file name is too long. File names can be no longer than 112 characters";
        }
        return;
    }

    async getFiles(): Promise<any[]> {
        const data = await AttachmentService.getFileList(this.model.itemTypeId, this.model.itemId);
        return data || [];
    }
}

export interface AttachmentItem {
    is_native: boolean;
    itemId: string;
    itemName: string;
    itemTypeId: number;
    objectId: string;
    objectTypeId: number;
}
